<template>
  <div
    class="chatui-search-selector-panel absolute top-[30px] left-0 width-[248px] rounded-[5px] p-[8px] bg-fl-nv z-20"
    @click.stop
    :style="posStyle"
  >
    <Input
      ref="search-input"
      class="h-[32px] px-[8px] bg-transparent"
      v-model="searchStr"
      icon="icon-search-bold"
      :placeholder="placeholder"
      clearable
      v-if="showSearch"
    />
    <div 
      class="chatui-search-selector-list max-w-[280px] overflow-y-scroll relative" 
      :class="{'has-search': showSearch}"
      ref="scroller"
    >
      <template v-if="options.length > 0">
        <template v-for="(group, index) in options">
          <div
            class="chatui-search-selector-item"
            :class="{ 
              active: activeId === item[useKeyField.id],
              disable: activeId !== item[useKeyField.id] && disableIds.includes(item[useKeyField.id]),
              'no-select': noSelectIds.includes(item[useKeyField.id])
            }"
            @click="handleChoose(item)"
            :key="item[useKeyField.id]"
            v-for="item in group"
          >
            <ChatImg
              class="chatui-search-selector-img"
              :src="item[useKeyField.img]"
              :quality="30"
              v-if="item[useKeyField.img]"
            />
            <i
              v-if="!item[useKeyField.img] && item[useKeyField.icon]"
              class="chatui-search-selector-icon iconfont"
              :class="item[useKeyField.icon]"
            ></i>
            <div
              class="chatui-search-selector-name ellipsis"
              v-emoji-html="{text: item[useKeyField.name], className: 'w-[12px]'}"
              v-if="item[useKeyField.name]"
            ></div>
            <div
              class="chatui-search-selector-desc ellipsis"
              v-emoji-html="{text: item[useKeyField.desc], className: 'w-[12px]'}"
              v-if="item[useKeyField.desc]"
            ></div>
            <i
              v-if="item[useKeyField.icon_suffix]"
              class="chatui-search-selector-icon iconfont icon-suffix"
              :class="item[useKeyField.icon_suffix]"
            ></i>
            <slot name="active-module"></slot>
            <template v-if="!$slots['active-module']">
              <i
                class="iconfont icon-correct-bold"
                v-if="activeId === item[useKeyField.id]"
              ></i>
            </template>
          </div>
          <div class="my-[6px] w-full h-[1px] bg-op-1" v-if="index < options.length - 1"></div>
        </template>
      </template>
      <p class="text-[14px] leading-[18px] text-op-4 text-center my-[24px] mb-[16px]" v-else>
        {{emptyText}}
      </p>
    </div>
  </div>
</template>

<script>
import { PopupManager } from 'element-ui/lib/utils/popup'
import { throttle } from '_lib/utils/utils'
export default {
  name: 'SearchSelector',
  props: {
    placeholder: {
      type: String,
      default: '搜索'
    },
    emptyText: {
      type: String,
      default: '暂无数据'
    },
    options: {
      type: Array,
      default: () => [],
    },
    activeId: {
      type: [String, Number],
      default: '',
    },
    disableIds: {
      type: Array,
      default: () => [],
    },
    noSelectIds: {
      type: Array,
      default: () => [],
    },
    keyFields: {
      type: Object,
      default: () => ({}),
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    initSearchText: {
      type: String,
      default: '',
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    trigger: '',
    needInvert: {
      type: Boolean,
      default: false,
    },
    invertThreshold: {
      type: Number,
      default: 310,
    },
    enablePagination: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      searchStr: this.initSearchText,
      posStyle: {},
    }
  },
  created () {
    this.resizeListener()
  },
  mounted () {
    document.addEventListener('click', this.clickListener)
    if (this.showSearch && this.autoFocus) {
      this.$refs['search-input'].focus()
    }
    if (this.trigger && this.posStyle.position) {
      document.body.appendChild(this.$el)
    }
    window.addEventListener('resize', this.resizeListener)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.clickListener)
    clearTimeout(this.searchStrTimer)
    this.searchStrTimer = null
    window.removeEventListener('resize', this.resizeListener)
    this.$refs['scroller'].removeEventListener('scroll', this.handleScroll)
  },
  watch: {
    searchStr(val) {
      if (this.searchStrTimer) {
        clearTimeout(this.searchStrTimer)
      }
      this.searchStrTimer = setTimeout(() => {
        this.$emit('search', val)
      }, 300)
    },
    enablePagination: {
      handler(v) {
        this.$nextTick(() => {
          if (v) {
            this.$refs['scroller'].addEventListener('scroll', this.handleScroll)
          } else {
            this.$refs['scroller'].removeEventListener('scroll', this.handleScroll)
          }
        })
      },
      immediate: true,
    }
  },
  computed: {
    useKeyField() {
      return {
        id: 'id',
        name: 'name',
        desc: 'desc',
        img: 'img',
        icon: 'icon',
        icon_suffix: 'icon_suffix',
        ...this.keyFields
      }
    }
  },
  methods: {
    handleChoose(item) {
      if (this.activeId !== item[this.useKeyField.id] && (this.disableIds.includes(item[this.useKeyField.id]) || this.noSelectIds.includes(item[this.useKeyField.id]))) return
      this.$emit('choose', item)
    },
    clickListener (e) {
      if (!this.$el.contains(e.target)) {
        this.$emit('close')
      }
    },
    resizeListener () {
      if (this.trigger) {
        // 根据触发器计算定位，并挂在body下面
        const el = this.trigger
        const rect = el.getBoundingClientRect()
        this.posStyle = {
          position: 'fixed',
          top: `${rect.top + rect.height + 4}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          'z-index': PopupManager.nextZIndex()
        }

        if(this.needInvert) {
          const inputWithPaddingHeight = 40;
          const bottomMarginWithPadding = 24;
          const threshold = this.invertThreshold;
          const realHeight = window.innerHeight - rect.top - rect.height - 4 - inputWithPaddingHeight - bottomMarginWithPadding
          const isNeedInvertRes = realHeight < threshold
          if(isNeedInvertRes) {
            this.posStyle['top'] = `${rect.top - threshold - 4}px`
          }
        } 
      }
    },
    handleScroll: throttle(function(e) {
      const target = e.target
      if (target) {
        if (target.scrollHeight - target.scrollTop - target.clientHeight < 100) {
          this.$emit('loadMore')
        }
      }
    }, 200),
  },
}
</script>

<style lang="scss">
.chatui-search-selector-panel {
  box-shadow: 0px 3px 15px 0px #00000059;
  .chatui-search-selector-list {
    width: calc(100% + 8px);
    &.has-search {
      padding-top: 13px;
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 6px;
        width: 100%;
        height: 1px;
        background: $d-op-1;
      }
    }
    .chatui-search-selector-item {
      height: 28px;
      margin: 2px 0;
      padding: 0 8px;
      display: flex;
      align-items: center;
      cursor: pointer;
      border-radius: 5px;
      &:not(.no-select):hover {
        background: $d-op-2;
      }
      &.active {
        position: relative;
        .chatui-search-selector-icon,
        .chatui-search-selector-name {
          color: $d-tx-1;
        }
        .chatui-search-selector-icon {
          font-weight: 400;
        }
      }
      &.disable {
        opacity: .4;
        cursor: not-allowed;
      }
      &.no-select {
        cursor: default;
        height: 26px;
        .chatui-search-selector-name { 
          font-size: 12px;
          font-weight: 700;
          line-height: 16px;
          color: $d-op-4;
        }
      }
      .chatui-search-selector-icon {
        margin-right: 6px;
        font-size: 16px;
        width: 16px;
        height: 16px;
        border-radius: 3px;
        color: $d-tx-4;
      }
      .icon-suffix {
          margin-left: 6px;
        }
      .chatui-search-selector-img {
        width: 22px;
        height: 22px;
        border-radius: 3px;
        margin-right: 6px;
      }
      .chatui-search-selector-name {
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
        color: $d-tx-2;
        flex: 1;
        width: 0;
        // max-width: 132px;
      }

      .chatui-search-selector-desc {
        margin-left: 4px;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        color: $d-tx-4;
        transform: translateY(1px);
      }
      .icon-correct-bold {
        padding-left: 6px;
        // margin-left: auto;
        font-size: 16px;
        color: $d-bd-tx;
      }
    }
  }
}
</style>
